import { useState, useRef, useCallback } from "react";
import { Move } from "lucide-react";

export default function FocalPointPicker({ imageUrl, positionX = 50, positionY = 50, onChange, className = "" }) {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const updatePosition = useCallback((clientX, clientY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
    onChange?.(Math.round(x), Math.round(y));
  }, [onChange]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    updatePosition(e.clientX, e.clientY);
    const onMove = (ev) => updatePosition(ev.clientX, ev.clientY);
    const onUp = () => { setDragging(false); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleTouchStart = (e) => {
    setDragging(true);
    const touch = e.touches[0];
    updatePosition(touch.clientX, touch.clientY);
    const onMove = (ev) => { const t = ev.touches[0]; updatePosition(t.clientX, t.clientY); };
    const onEnd = () => { setDragging(false); window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onEnd); };
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
  };

  if (!imageUrl) return null;

  return (
    <div className={`relative ${className}`}>
      <p className="text-[10px] text-[#676664]/50 uppercase tracking-wider mb-1.5 flex items-center gap-1">
        <Move className="w-3 h-3" /> Drag to adjust focal point
      </p>
      <div
        ref={containerRef}
        className="relative overflow-hidden cursor-crosshair border border-[#282828]/15 select-none"
        style={{ height: "200px" }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        data-testid="focal-point-picker"
      >
        <img
          src={imageUrl}
          alt="Focal point preview"
          className="w-full h-full object-cover"
          style={{ objectPosition: `${positionX}% ${positionY}%` }}
          draggable={false}
        />
        {/* Focal point indicator */}
        <div
          className="absolute w-6 h-6 -ml-3 -mt-3 pointer-events-none z-10"
          style={{ left: `${positionX}%`, top: `${positionY}%` }}
        >
          <div className={`w-full h-full rounded-full border-2 border-white shadow-lg transition-transform ${dragging ? "scale-125" : ""}`}>
            <div className="w-2 h-2 bg-white rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
        </div>
        {/* Crosshair lines */}
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: dragging ? 0.3 : 0 }}>
          <div className="absolute bg-white/50" style={{ left: `${positionX}%`, top: 0, width: "1px", height: "100%" }} />
          <div className="absolute bg-white/50" style={{ left: 0, top: `${positionY}%`, width: "100%", height: "1px" }} />
        </div>
      </div>
      <p className="text-[9px] text-[#676664]/30 mt-1">Position: {positionX}% x {positionY}%</p>
    </div>
  );
}
